import Image from "next/image";
import { optimizeCloudinaryImg } from "@dreambigger/shared/src/utils";
import { CodeVerification } from "../../components";
import { StartTemplateProps } from "./types";
import { Form, Row, Col } from "antd";
import {
  Input,
  Button,
  MaskedInput,
} from "@dreambigger/design-system/src/components";
import { RightOutlined } from "@ant-design/icons";
import InlineButtonInput from "./InlineButtonInput";
import animationStyles from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { useSegment } from "@dreambigger/shared/src/hooks";

export default function V2Template({
  assets,
  brand,
  flow,
  loginForm,
  keyByLoginMode,
  formattedCred,
  emailInputRef,
  phoneInputRef,
  handleNext,
  isAdminMode,
  codeVerificationProps,
}: StartTemplateProps) {
  const maxWidth = "1800px";
  const segment = useSegment();

  const trackAnalytics = () => {
    segment.track({ action: "Logo Click", label: "Logo - Start" });
  };

  return (
    <div
      className="min-h-100vh pa-3 xl_ph-6 xxl_ph-0"
      style={{
        backgroundColor: "#f5f5f5",
        overflowX: "hidden",
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .v2-hero-image-wrapper-${flow.id} {
          flex: 0 0 300px;
          min-height: 300px;
        }
        @media (min-width: 768px) {
          .v2-hero-image-wrapper-${flow.id} {
            flex: 0 0 45%;
          }
        }
      `,
        }}
      />
      {/* Header - No animation */}
      <div
        className="mb-4 lg_mb-8"
        style={{
          maxWidth,
          margin: "0 auto",
        }}
      >
        <div
          className="flex items-center pb-3"
          style={{
            borderBottom: "1px solid #d1d5db",
          }}
        >
          {brand.primaryLogo &&
            (brand.primaryLogoUrl ? (
              <a
                href={brand.primaryLogoUrl}
                target="_blank"
                rel="noreferrer"
                onClick={trackAnalytics}
              >
                <img
                  src={optimizeCloudinaryImg(brand.primaryLogo)}
                  alt="Logo"
                  style={{
                    height:
                      brand.acquireLogoHeight ||
                      brand.primaryLogoHeight ||
                      "24px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </a>
            ) : (
              <img
                src={optimizeCloudinaryImg(brand.primaryLogo)}
                alt="Logo"
                style={{
                  height:
                    brand.acquireLogoHeight ||
                    brand.primaryLogoHeight ||
                    "24px",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            ))}
          <h2
            className="f-4 fwn dn md_db"
            style={{
              margin: 0,
              color: brand.secondaryTextColor || "#6b7280",
            }}
          >
            {flow.title}
          </h2>
        </div>
      </div>

      {/* Main Card */}
      <div className="lg_ph-7">
        <div
          className={`${animationStyles.slideInUp_f} flex flex-column md_flex-row`}
          style={{
            maxWidth,
            margin: "0 auto",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            minHeight: "500px",
          }}
        >
          {/* Left Content */}
          <div
            className="flex flex-column justify-center flex-1 pa-4 ph-2 md_pa-5 lg_pl-9"
            style={{
              background:
                assets.heroBackground === "secondaryColor"
                  ? brand.secondaryColor
                  : assets.heroBackground
                  ? `url(${assets.heroBackground})`
                  : `linear-gradient(180deg, rgba(${
                      assets.heroBackgroundColor1 || "249,249,249"
                    }) 0%, rgba(${
                      assets.heroBackgroundColor2 || "249,249,249"
                    }) 100%)`,
            }}
          >
            {/* Title */}
            <h1
              className={`textSecondary fwt lh-1 f-7 lg_f-9 mt-5 lg_mt-0 mb-0 ${animationStyles.fadeIn_f}`}
              dangerouslySetInnerHTML={{
                __html: assets.title ?? "",
              }}
            />

            {/* Subtitle */}
            {assets.subTitle && (
              <h2
                className={`textSecondary fwb lh-1 f-5 lg_f-7 mb-0 mt-2 ${animationStyles.fadeIn_m}`}
                style={{
                  color: brand.secondaryColor,
                }}
                dangerouslySetInnerHTML={{
                  __html: assets.subTitle,
                }}
              />
            )}

            {/* Description */}
            <div
              className={`textSecondary f-4 lg_f-5 lh-2 lg_mt-7 ${animationStyles.fadeIn_s}`}
              dangerouslySetInnerHTML={{
                __html: assets.description ?? "",
              }}
            />

            {/* Login Title */}
            {assets.loginTitle && (
              <div className={animationStyles.fadeIn_xs}>
                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                />
                <label
                  className="textSecondary f-5 fwb db mb-2"
                  dangerouslySetInnerHTML={{
                    __html: assets.loginTitle,
                  }}
                />
              </div>
            )}

            {/* Desktop - Inline Button Input */}
            <div className={`dn md_db ${animationStyles.fadeIn_xs}`}>
              <InlineButtonInput
                form={loginForm}
                loginMode={flow.financialInstitution.loginMode}
                keyByLoginMode={keyByLoginMode}
                formattedCred={formattedCred}
                loginPlaceholder={assets.loginPlaceholder}
                submitButtonText={assets.submitButtonText}
                emailInputRef={emailInputRef}
                phoneInputRef={phoneInputRef}
                onFinish={handleNext}
                isAdminMode={isAdminMode}
                wrapperClassName="mb-6 md_mb-4 mt-1"
                innerClassName="f-3 md_f-4 md_h-7"
              />
            </div>

            {/* Mobile - Separate input and button */}
            <div className={`db md_dn ${animationStyles.fadeIn_xs}`}>
              <Form
                form={loginForm}
                initialValues={{ [keyByLoginMode]: formattedCred }}
                onFinish={handleNext}
              >
                <Row gutter={[0, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      name={keyByLoginMode}
                      style={{ marginBottom: 0 }}
                    >
                      {flow.financialInstitution.loginMode === "email" ? (
                        <Input
                          placeholder={assets.loginPlaceholder}
                          type="email"
                          ref={emailInputRef}
                          className={`br-5 pa-3 ${
                            isAdminMode && "disabled notAllowed"
                          }`}
                          style={{ height: 54 }}
                          readOnly={isAdminMode}
                          required
                        />
                      ) : (
                        <MaskedInput
                          placeholder={assets.loginPlaceholder}
                          mask="(000) 000-0000"
                          name="phone"
                          className={`br-5 pa-3 ${
                            isAdminMode && "disabled notAllowed"
                          }`}
                          style={{ height: 54 }}
                          ref={phoneInputRef}
                          readOnly={isAdminMode}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="lift h-8 ph-6"
                      >
                        {assets.submitButtonText} <RightOutlined />
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>

          {/* Right Image */}
          {assets.heroImage && (
            <div className={`relative v2-hero-image-wrapper-${flow.id}`}>
              <Image
                src={optimizeCloudinaryImg(assets.heroImage)}
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                priority={true}
                alt="Hero image"
              />
            </div>
          )}
        </div>

        {/* Terms Section */}
        {(assets.landingTermsTitle || assets.landingTermsText) && (
          <div
            className={animationStyles.fadeIn_xs}
            style={{
              maxWidth,
              margin: "32px auto 0",
              padding: "0 24px",
            }}
          >
            {assets.landingTermsTitle && (
              <h3
                className="fwb"
                style={{
                  fontSize: "16px",
                  color: "#111827",
                  marginBottom: "8px",
                }}
                dangerouslySetInnerHTML={{
                  __html: assets.landingTermsTitle,
                }}
              />
            )}
            {assets.landingTermsText && (
              <div
                className="lh-2"
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                }}
                dangerouslySetInnerHTML={{
                  __html: assets.landingTermsText,
                }}
              />
            )}
          </div>
        )}
      </div>

      <CodeVerification {...codeVerificationProps} />
    </div>
  );
}
